import { CHAIN_IDS } from "../../constants/evm"
import type { SupportedChainName } from "../../types/base"
import { isEvmAddress, validateAddress } from "../validateAddress"
import {
  isSupportedQrBitcoinAddress,
  normalizeQrBitcoinAddress,
} from "./bitcoinAddress"
import { detectChainFromAddress } from "./detectChainFromAddress"

export type RejectReason =
  | "wc_not_supported"
  | "evm_unsupported_chain"
  | "unsupported_format"

export type ParsedAmount =
  | { kind: "decimal"; value: string }
  | { kind: "rawTokenUnits"; value: string }

export type ParsedPayment = {
  recipient: string
  chain: SupportedChainName
  tokenAddress: string | null
  amount: ParsedAmount | null
}

export type ParseResult =
  | { kind: "accepted"; payment: ParsedPayment }
  | { kind: "rejected"; reason: RejectReason }

const EIGHTEEN_ZEROS = BigInt("1000000000000000000")
// EIP-681 payloads without @chain_id do not identify a network.
const DEFAULT_EIP681_CHAIN: SupportedChainName = "eth"
const EIP681_CHAIN_BY_ID = Object.fromEntries(
  Object.entries(CHAIN_IDS).flatMap(([chain, chainId]) =>
    chainId == null ? [] : [[String(chainId), chain]]
  )
) as Record<string, SupportedChainName>

function isNonNegativeInteger(s: string): boolean {
  return /^\d+$/.test(s)
}

function isNonNegativeDecimal(s: string): boolean {
  return /^\d+(\.\d+)?$/.test(s)
}

function weiToEthDecimal(weiStr: string): string | null {
  if (!isNonNegativeInteger(weiStr)) {
    return null
  }

  const wei = BigInt(weiStr)
  const whole = wei / EIGHTEEN_ZEROS
  const remainder = wei % EIGHTEEN_ZEROS

  if (remainder === 0n) return whole.toString()

  const fracFull = remainder.toString().padStart(18, "0").replace(/0+$/, "")
  return `${whole}.${fracFull}`
}

function accepted(payment: ParsedPayment) {
  return { kind: "accepted", payment } as const
}

function rejected(reason: RejectReason) {
  return { kind: "rejected", reason } as const
}

function chainFromEip681ChainId(
  chainId: string | null
): SupportedChainName | null {
  if (chainId == null) return DEFAULT_EIP681_CHAIN

  const normalized = chainId.trim()
  if (!isNonNegativeInteger(normalized)) return null

  return EIP681_CHAIN_BY_ID[normalized] ?? null
}

function parseEip681(rest: string): ParseResult {
  const qIdx = rest.indexOf("?")
  const path = qIdx === -1 ? rest : rest.slice(0, qIdx)
  const query = qIdx === -1 ? "" : rest.slice(qIdx + 1)
  const params = new URLSearchParams(query)

  const slashIdx = path.indexOf("/")

  if (slashIdx === -1) {
    const atIdx = path.indexOf("@")
    const recipient = (atIdx === -1 ? path : path.slice(0, atIdx)).trim()
    const chainId = atIdx === -1 ? null : path.slice(atIdx + 1)
    const chain = chainFromEip681ChainId(chainId)
    if (chain == null) {
      return rejected("evm_unsupported_chain")
    }
    if (!isEvmAddress(recipient)) {
      return rejected("unsupported_format")
    }
    const valueStr = params.get("value")
    const weiDecimal = valueStr ? weiToEthDecimal(valueStr) : null
    const amount: ParsedAmount | null = weiDecimal
      ? { kind: "decimal", value: weiDecimal }
      : null
    return accepted({ recipient, chain, tokenAddress: null, amount })
  }

  const funcName = path.slice(slashIdx + 1)
  if (funcName !== "transfer") return rejected("unsupported_format")

  const tokenPart = path.slice(0, slashIdx)
  const atIdx = tokenPart.indexOf("@")
  const tokenAddress = (
    atIdx === -1 ? tokenPart : tokenPart.slice(0, atIdx)
  ).trim()
  const chainId = atIdx === -1 ? null : tokenPart.slice(atIdx + 1)
  const chain = chainFromEip681ChainId(chainId)
  if (chain == null) {
    return rejected("evm_unsupported_chain")
  }
  if (!isEvmAddress(tokenAddress)) {
    return rejected("unsupported_format")
  }
  const recipientRaw = params.get("address") ?? ""
  const recipient = recipientRaw.trim()
  if (!isEvmAddress(recipient)) {
    return rejected("unsupported_format")
  }
  const uint256 = params.get("uint256")
  const amount: ParsedAmount | null =
    uint256 && isNonNegativeInteger(uint256)
      ? { kind: "rawTokenUnits", value: uint256 }
      : null

  return accepted({ recipient, chain, tokenAddress, amount })
}

function parseBip21(rest: string): ParseResult {
  const qIdx = rest.indexOf("?")
  const recipient = normalizeQrBitcoinAddress(
    qIdx === -1 ? rest : rest.slice(0, qIdx)
  )
  const query = qIdx === -1 ? "" : rest.slice(qIdx + 1)
  const params = new URLSearchParams(query)

  if (!isSupportedQrBitcoinAddress(recipient)) {
    return rejected("unsupported_format")
  }

  const amountStr = params.get("amount")
  const amount: ParsedAmount | null =
    amountStr && isNonNegativeDecimal(amountStr)
      ? { kind: "decimal", value: amountStr }
      : null

  return accepted({ recipient, chain: "bitcoin", tokenAddress: null, amount })
}

function parseSolanaPay(rest: string): ParseResult {
  const qIdx = rest.indexOf("?")
  const recipient = (qIdx === -1 ? rest : rest.slice(0, qIdx)).trim()
  const query = qIdx === -1 ? "" : rest.slice(qIdx + 1)
  const params = new URLSearchParams(query)

  if (!validateAddress(recipient, "solana")) {
    return rejected("unsupported_format")
  }

  const amountStr = params.get("amount")
  const amount: ParsedAmount | null =
    amountStr && isNonNegativeDecimal(amountStr)
      ? { kind: "decimal", value: amountStr }
      : null
  const tokenAddressRaw = params.get("spl-token")
  let tokenAddress: string | null = null
  if (tokenAddressRaw != null) {
    tokenAddress = tokenAddressRaw.trim()
    if (!validateAddress(tokenAddress, "solana")) {
      return rejected("unsupported_format")
    }
  }

  return accepted({
    recipient,
    chain: "solana",
    tokenAddress,
    amount,
  })
}

export function parsePaymentUri(input: string): ParseResult {
  const trimmed = input.trim()

  if (trimmed.length === 0) return rejected("unsupported_format")

  const colonIdx = trimmed.indexOf(":")
  if (colonIdx !== -1) {
    const scheme = trimmed.slice(0, colonIdx).toLowerCase()
    const rest = trimmed.slice(colonIdx + 1)

    if (scheme === "wc") return rejected("wc_not_supported")
    if (scheme === "ethereum") return parseEip681(rest)
    if (scheme === "bitcoin") return parseBip21(rest)
    if (scheme === "solana") return parseSolanaPay(rest)

    return rejected("unsupported_format")
  }

  const detection = detectChainFromAddress(trimmed)

  if (detection === "unknown") return rejected("unsupported_format")

  const chain: SupportedChainName =
    detection === "evm_ambiguous" ? "eth" : detection

  return accepted({
    recipient:
      chain === "bitcoin" ? normalizeQrBitcoinAddress(trimmed) : trimmed,
    chain,
    tokenAddress: null,
    amount: null,
  })
}
